import { Controller } from '@hotwired/stimulus';
import Sortable from 'sortablejs';
import { formatHours } from '../shared/util/format';
import i18n from '../../i18n.js';

export default class extends Controller {
  static targets = [
    'formTemplate',
    'form',
    'name',
    'id',
    'hours',
    'progress',
    'currentHours',
    'totalHours',
    'tooManyHoursText'
  ];
  static values = {
    categoryId: Number,
    sectorId: Number,
    schoolyearId: Number,
    percentage: Number,
    categoryDefaultOnly: Boolean,
  };

  connect() {
    this.subHourBanks = gon.sub_hour_banks[this.categoryIdValue] ?? [];
    this.suggestions = gon.sub_hour_bank_suggestions ?? [];
    this.buildForm();
    this.updateHours();
    this.sortable = new Sortable(this.formTarget, {
      handle: '.drag-handle',
      onEnd: () => this.updateHiddenInputs(),
    });

    if (this.categoryDefaultOnlyValue) {
      // Hide the column header (name/duration labels) — no visible rows to label.
      // The add button remains visible so users can still create real subcategories.
      const columnHeader = this.element.querySelector('.is-flex.is-size-6');

      if (columnHeader) {
        columnHeader.hidden = true;
      }
    }
  }

  updateHours() {
    const hourBankKey = [this.categoryIdValue, this.sectorIdValue, this.schoolyearIdValue].join(',');
    const hourBank = window.gon.hour_banks[hourBankKey];

    if (!hourBank) {
      return;
    }

    const currentHours = this.hoursTargets.reduce((acc, hourTarget) => (Number(hourTarget.value) || 0) + acc, 0);
    const totalHours = Math.round((hourBank.hours * this.percentageValue) / 100);

    setAttributes(this.progressTarget, { max: totalHours, value: currentHours });
    this.currentHoursTarget.textContent = formatHours(currentHours);
    this.totalHoursTarget.textContent = formatHours(totalHours);
    const excessHours = currentHours - totalHours;

    this.tooManyHoursTextTarget.textContent = i18n.template(gon.t.too_many_hours, { hours: formatHours(excessHours) });
    this.tooManyHoursTextTarget.parentElement.hidden = excessHours <= 0;

    this.updateHiddenInputs();
  }

  deleteRow(event) {
    const row = event.target.closest('.form-sub-hour-bank-row');
    const select = row.querySelector('[data-sub-hour-bank-form-target="name"]');

    if (select && $(select).data('select2')) {
      $(select).select2('destroy');
    }
    row.remove();
    this.updateHours();
  }

  updateHiddenInputs() {
    // Remove existing hidden inputs except category default ones (handled separately)
    const existingInputs = this.element.querySelectorAll('input[name="sub_hour_bank[]"]:not([data-category-default])');

    existingInputs.forEach((input) => input.remove());

    // Create new hidden inputs from current form state
    this.nameTargets.forEach((nameTarget, index) => {
      const idTarget = this.idTargets[index];
      const hoursTarget = this.hoursTargets[index];

      const hiddenInput = document.createElement('input');

      hiddenInput.type = 'hidden';
      hiddenInput.name = 'sub_hour_bank[]';
      hiddenInput.value = JSON.stringify({
        'id': idTarget?.value || '',
        'category_id': this.categoryIdValue,
        'name': nameTarget.value,
        'hours': hoursTarget.value,
        'order': index
      });

      this.formTarget.appendChild(hiddenInput);
    });
  }

  initSelect2(selectElement, initialValue, hasError = false) {
    $(selectElement).select2({
      tags: true,
      width: '100%',
      placeholder: '',
      allowClear: true,
      data: [{ id: '', text: '' }, ...this.suggestions.map((name) => ({ id: name, text: name }))],
    });

    if (initialValue) {
      // Check if the value exists in suggestions
      const exists = this.suggestions.includes(initialValue);

      if (!exists) {
        // Add as a new option if it doesn't exist
        const newOption = new Option(initialValue, initialValue, true, true);

        $(selectElement).append(newOption);
      }
      $(selectElement).val(initialValue).trigger('change');
    } else {
      // Ensure the select is blank when no initial value is provided
      $(selectElement).val(null).trigger('change');
    }

    // Add error styling to Select2 container if field has error
    if (hasError) {
      const $select2Container = $(selectElement).next('.select2-container');

      $select2Container.find('.select2-selection').addClass('is-danger');
    }

    $(selectElement).on('change', () => {
      // Remove error styling when user changes selection
      const $select2Container = $(selectElement).next('.select2-container');

      $select2Container.find('.select2-selection').removeClass('is-danger');
      this.updateHours();
    });
  }

  addLine(_event, id, name, hours, errors = []) {
    const newLine = this.formTemplateTarget.content.cloneNode(true);

    newLine.querySelector('[data-sub-hour-bank-form-target="id"]').value = id ?? '';

    const hoursInput = newLine.querySelector('[data-sub-hour-bank-form-target="hours"]');

    hoursInput.value = hours ?? '';
    hoursInput.addEventListener('input', (event) => {
      if (Number(event.target.value) < 0) {
        event.target.value = 0;
      }
      // Remove error styling when user starts typing
      event.target.classList.remove('is-danger');
      this.updateHours();
    });

    // Add error styling for hours field
    if (errors.includes('hours')) {
      hoursInput.classList.add('is-danger');
    }

    newLine.querySelector('[data-action="click->sub-hour-bank-form#deleteRow"]').addEventListener('click', (e) => {
      this.deleteRow(e);
    });

    this.formTarget.appendChild(newLine);

    // Initialize Select2 on the newly added select element
    const selectElement = this.formTarget.lastElementChild.querySelector('[data-sub-hour-bank-form-target="name"]');

    this.initSelect2(selectElement, name, errors.includes('name'));

    this.updateHours();
  }

  buildForm() {
    this.subHourBanks.forEach((subHourBank) => {
      if (subHourBank.category_default) {
        // Silently preserve the category default via a hidden input without
        // rendering a visible row — users shouldn't see or edit this catch-all.
        this.appendDefaultHiddenInput(subHourBank);
      } else {
        this.addLine(null, subHourBank.id, subHourBank.name, subHourBank.hours, subHourBank.errors || []);
      }
    });
  }

  appendDefaultHiddenInput(subHourBank) {
    const hiddenInput = document.createElement('input');

    hiddenInput.type = 'hidden';
    hiddenInput.name = 'sub_hour_bank[]';
    hiddenInput.dataset.categoryDefault = 'true';
    hiddenInput.value = JSON.stringify({
      id: subHourBank.id,
      category_id: this.categoryIdValue,
      name: subHourBank.name,
      hours: subHourBank.hours,
      order: subHourBank.order ?? 0,
    });
    this.formTarget.appendChild(hiddenInput);
  }

  clearAll() {
    // Remove all rows
    const rows = this.formTarget.querySelectorAll('.form-sub-hour-bank-row');

    rows.forEach((row) => {
      const select = row.querySelector('[data-sub-hour-bank-form-target="name"]');

      if (select && $(select).data('select2')) {
        $(select).select2('destroy');
      }
      row.remove();
    });
    this.updateHours();
  }

  addLineWithData(data) {
    this.addLine(null, data.id, data.name, data.hours);
  }
}

function setAttributes(element, attributes) {
  Object.keys(attributes).forEach((key) => {
    element.setAttribute(key, attributes[key]);
  })
}
